import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Zap, Store } from 'lucide-react';
import { Button } from './ui/button';
import { featureCards } from '../data/mock';
import { trackCtaClick, track } from '../lib/analytics';
import { resolveHeroVariant } from '../lib/heroVariants';

const iconMap = { ShieldCheck, Zap, Store };

export default function Hero() {
  // Resolve once on mount. SSR-safe: defaults to the generic variant on the
  // server, hydrates with the personalized variant on the client. This is
  // fine for paid-traffic landing pages where SEO isn't the goal — and for
  // direct/organic traffic the default is what we'd serve anyway.
  const [variant, setVariant] = useState(() => resolveHeroVariant());
  useEffect(() => {
    const v = resolveHeroVariant();
    setVariant(v);
    // Tag the dataLayer so we can slice GA4 conversion reports by variant
    // and prove which ad-aware copy actually converts best.
    if (v.key !== 'default') {
      track('hero_variant_view', { variant: v.key });
    }
  }, []);

  return (
    <section className="relative overflow-hidden">
      {/* Soft brand backdrop */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-50/60 via-white to-white" />
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-indigo-200/30 blur-3xl" />
        <div className="absolute top-40 -left-32 w-[420px] h-[420px] rounded-full bg-blue-200/30 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 pb-20">
        <div className="text-center max-w-5xl mx-auto animate-fade-up" data-testid={`hero-variant-${variant.key}`}>
          {variant.preheadline && (
            <p className="mb-4 inline-block px-4 py-1.5 rounded-full bg-indigo-100 text-indigo-700 text-sm font-bold uppercase tracking-wider">
              {variant.preheadline}
            </p>
          )}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            {variant.headline}{' '}
            <span className="brand-gradient-text">{variant.highlight}</span>
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-slate-600 font-medium">
            {variant.subheadline}
          </p>

          <div className="mt-8 inline-flex items-center justify-center px-6 py-3.5 rounded-xl border-2 border-indigo-200 bg-indigo-50/60">
            <span className="text-lg sm:text-xl font-bold text-slate-900">
              {variant.callout}
            </span>
          </div>

          <div className="mt-10">
            <Button
              asChild
              size="lg"
              className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-12 px-7 text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 group"
            >
              {variant.ctaHref.startsWith('/') ? (
                // Internal route (e.g. /compare/shipbob) — use Link so we
                // get an SPA navigation instead of a full reload.
                <Link
                  to={variant.ctaHref}
                  className="inline-flex items-center gap-2"
                  onClick={() => trackCtaClick({
                    label: variant.cta,
                    location: 'hero',
                    destination: variant.ctaHref,
                    variant: variant.key,
                  })}
                  data-testid="hero-cta-get-started"
                >
                  {variant.cta}
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              ) : (
                // Same-page anchor (e.g. #contact) — plain anchor is fine.
                <a
                  href={variant.ctaHref}
                  className="inline-flex items-center gap-2"
                  onClick={() => trackCtaClick({
                    label: variant.cta,
                    location: 'hero',
                    destination: variant.ctaHref,
                    variant: variant.key,
                  })}
                  data-testid="hero-cta-get-started"
                >
                  {variant.cta}
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </a>
              )}
            </Button>
          </div>
        </div>

        {/* Feature cards */}
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {featureCards.map((card, idx) => {
            const Icon = iconMap[card.icon];
            return (
              <div
                key={card.title}
                className="group relative bg-white border border-slate-200 rounded-2xl p-7 hover:border-indigo-300 hover:shadow-lg transition-all duration-200"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center mb-5 group-hover:bg-indigo-100 transition-colors">
                  <Icon className="w-6 h-6 text-indigo-600" strokeWidth={2} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{card.title}</h3>
                <p className="text-slate-600 leading-relaxed">{card.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
